import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Cloud,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Loader2,
  MapPin,
  Sun,
} from "lucide-react";
import { cn } from "@/lib/utils";

const DUBAI = { lat: 25.2048, lon: 55.2708, labelKey: "dashboard.weatherDubai" };
const CACHE_KEY = "rc-weather-cache-v1";
const CACHE_TTL_MS = 20 * 60 * 1000;

function weatherFromCode(code) {
  if (code === 0) return { labelKey: "dashboard.weatherClear", Icon: Sun };
  if (code === 1 || code === 2) return { labelKey: "dashboard.weatherPartlyCloudy", Icon: CloudSun };
  if (code === 3) return { labelKey: "dashboard.weatherCloudy", Icon: Cloud };
  if (code === 45 || code === 48) return { labelKey: "dashboard.weatherFog", Icon: CloudFog };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) {
    return { labelKey: "dashboard.weatherRain", Icon: CloudRain };
  }
  if ([71, 73, 75, 77, 85, 86].includes(code)) {
    return { labelKey: "dashboard.weatherSnow", Icon: CloudSnow };
  }
  if ([95, 96, 99].includes(code)) {
    return { labelKey: "dashboard.weatherStorm", Icon: CloudLightning };
  }
  return { labelKey: "dashboard.weatherCloudy", Icon: Cloud };
}

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt || Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...payload, fetchedAt: Date.now() }));
  } catch {
    /* ignore quota */
  }
}

async function fetchWeather(lat, lon) {
  const url =
    `https://api.open-meteo.com/v1/forecast` +
    `?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weather_code` +
    `&timezone=auto`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("weather_failed");
  const json = await res.json();
  return {
    temp: json?.current?.temperature_2m,
    code: json?.current?.weather_code ?? 3,
  };
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("no_geo"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 8000,
      maximumAge: 30 * 60 * 1000,
    });
  });
}

/**
 * Compact clock + weather glance card for the Command Center.
 * Weather uses free Open-Meteo; geolocation optional (falls back to Dubai).
 */
export default function ClockWeatherWidget({ timeZone, className, variant = "card" }) {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const [weather, setWeather] = useState(() => readCache());
  const [loading, setLoading] = useState(!readCache());
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const cached = readCache();
      if (cached) {
        if (!cancelled) {
          setWeather(cached);
          setUsingFallback(Boolean(cached.fallback));
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      let coords = { ...DUBAI };
      let fallback = true;

      try {
        const pos = await getPosition();
        coords = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          labelKey: "dashboard.weatherLocal",
        };
        fallback = false;
      } catch {
        coords = { ...DUBAI };
        fallback = true;
      }

      try {
        const data = await fetchWeather(coords.lat, coords.lon);
        const payload = {
          temp: data.temp,
          code: data.code,
          labelKey: coords.labelKey,
          fallback,
        };
        writeCache(payload);
        if (!cancelled) {
          setWeather(payload);
          setUsingFallback(fallback);
        }
      } catch {
        if (!cancelled) {
          setWeather(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const locale = i18n.language?.startsWith("ar") ? "ar-AE" : undefined;
  const clockOpts = useMemo(
    () => ({
      ...(timeZone ? { timeZone } : {}),
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }),
    [timeZone],
  );
  const dateOpts = useMemo(
    () => ({
      ...(timeZone ? { timeZone } : {}),
      weekday: "long",
      month: "short",
      day: "numeric",
    }),
    [timeZone],
  );

  const timeStr = now.toLocaleTimeString(locale, clockOpts);
  const dateStr = now.toLocaleDateString(locale, dateOpts);
  const meta = weatherFromCode(weather?.code ?? 3);
  const WeatherIcon = meta.Icon;

  return (
    <div
      className={cn(
        "rc-glance",
        variant === "bar" && "rc-glance--bar",
        variant === "inline" && "rc-glance--inline",
        className,
      )}
      data-testid="clock-weather-widget"
    >
      <div className="rc-glance-clock">
        <p className="rc-glance-eyebrow">{t("dashboard.localTime")}</p>
        <p className="rc-glance-time" data-testid="dashboard-clock">
          {timeStr}
        </p>
        <p className="rc-glance-date">{dateStr}</p>
      </div>
      <div className="rc-glance-weather">
        {loading ? (
          <div className="flex items-center gap-2 text-white/55 text-[12px]">
            <Loader2 size={14} className="animate-spin" />
            {t("dashboard.weatherLoading")}
          </div>
        ) : weather?.temp != null ? (
          <>
            <div className="flex items-center gap-2">
              <span className="rc-glance-weather-icon">
                <WeatherIcon size={18} />
              </span>
              <span className="rc-glance-temp" data-testid="dashboard-temp">
                {Math.round(Number(weather.temp))}°
              </span>
            </div>
            <p className="rc-glance-condition">{t(meta.labelKey)}</p>
            <p className="rc-glance-loc">
              <MapPin size={11} />
              {usingFallback ? t("dashboard.weatherDubai") : t(weather.labelKey || "dashboard.weatherLocal")}
            </p>
          </>
        ) : (
          <p className="text-[12px] text-white/50">{t("dashboard.weatherUnavailable")}</p>
        )}
      </div>
    </div>
  );
}
